// =============================================================
// SAAppVersion — Super-admin app version management
// -------------------------------------------------------------
// Lets the super admin bump the running app version + build
// number, update the release notes, and stamps a fresh
// `lastUpdatedAt` server-side so every tenant sees the change
// in their sidebar footer chip.
//
// All other tenants read this same record via /api/app/version
// so updating here instantly refreshes every user's badge.
// =============================================================

import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import AlertToast from '../../components/common/AlertToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useVersion } from '../../context/VersionContext';
import api from '../../services/api';

export default function SAAppVersion() {
  const { refetch } = useVersion();
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState({ show: false, msg: '', type: 'success' });
  const [form,    setForm]    = useState({
    version: '',
    buildNumber: '',
    releaseNotes: '',
    updatedBy: 'Super Admin',
  });
  const [meta, setMeta] = useState({ lastUpdatedAt: null, lastUpdatedBy: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/app/version');
      if (r.data?.success && r.data?.data) {
        const d = r.data.data;
        setForm({
          version:      d.version      || '',
          buildNumber:  d.buildNumber  || '',
          releaseNotes: d.releaseNotes || '',
          updatedBy:    d.updatedBy    || 'Super Admin',
        });
        setMeta({
          lastUpdatedAt: d.lastUpdatedAt || null,
          lastUpdatedBy: d.updatedBy     || '',
        });
      }
    } catch (e) {
      setToast({ show: true, msg: 'Failed to load current version info.', type: 'danger' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.version.trim()) {
      setToast({ show: true, msg: 'Version is required (e.g. 1.2.0).', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const r = await api.put('/app/version', {
        version:      form.version.trim(),
        buildNumber:  form.buildNumber.trim(),
        releaseNotes: form.releaseNotes,
        updatedBy:    form.updatedBy || 'Super Admin',
      });
      if (r.data?.success) {
        setToast({ show: true, msg: 'Application version updated. All tenants will see the new value in their sidebar.', type: 'success' });
        // Refresh the local context so the badge updates immediately
        if (typeof refetch === 'function') await refetch();
        await load();
      } else {
        setToast({ show: true, msg: r.data?.message || 'Update failed.', type: 'danger' });
      }
    } catch (e) {
      setToast({ show: true, msg: e.response?.data?.message || e.message || 'Update failed.', type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }) : '—';

  // Quick-stamp: re-write the same payload so the server-side
  // `lastUpdatedAt = new Date()` fires, but the version/build/notes
  // stay exactly as they are. Useful when the SA deploys small
  // hot-fixes without bumping the semver.
  const quickStamp = async () => {
    if (!form.version) {
      setToast({ show: true, msg: 'Set a version first, then re-stamp.', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const r = await api.put('/app/version', {
        version:      form.version.trim(),
        buildNumber:  form.buildNumber.trim(),
        releaseNotes: form.releaseNotes,
        updatedBy:    form.updatedBy || 'Super Admin',
      });
      if (r.data?.success) {
        setToast({ show: true, msg: `Re-stamped to ${fmtDate(new Date())}.`, type: 'success' });
        if (typeof refetch === 'function') await refetch();
        await load();
      } else {
        setToast({ show: true, msg: r.data?.message || 'Re-stamp failed.', type: 'danger' });
      }
    } catch (e) {
      setToast({ show: true, msg: e.response?.data?.message || e.message || 'Re-stamp failed.', type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <PageHeader
        title="Application Version"
        subtitle="Bump the version + release notes so tenants know what's new"
        icon="bi-tag"
      />

      <div className="alert alert-info d-flex align-items-start gap-2 py-2 mb-3" style={{ fontSize: 13 }}>
        <i className="bi bi-info-circle-fill mt-1" />
        <div>
          <strong>How this works.</strong>{' '}
          The version + last-updated date are a <em>manual stamp</em> set by you after each deployment.
          Deploys in your local repo don't auto-update this page — once you push your changes
          to the server, come here and either fill in the form (for a new release) or hit{' '}
          <strong>Quick re-stamp</strong> to bump the timestamp without changing the version.
          Every tenant's sidebar updates instantly.
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <form onSubmit={save}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Version <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      placeholder="e.g. 1.2.0"
                      value={form.version}
                      onChange={e => set('version', e.target.value)}
                      required
                    />
                    <small className="text-muted">Semantic version (e.g. 1.0.0, 2.3.1)</small>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Build Number</label>
                    <input
                      className="form-control"
                      placeholder="e.g. 2024.05.18-r1"
                      value={form.buildNumber}
                      onChange={e => set('buildNumber', e.target.value)}
                    />
                    <small className="text-muted">Optional commit/release tag</small>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-medium">Release Notes</label>
                    <textarea
                      className="form-control"
                      rows={8}
                      placeholder={'• New: Payment receipt PDF + branded email\n• Fix: attendance clock-out correction\n• Improved: dashboard analytics'}
                      value={form.releaseNotes}
                      onChange={e => set('releaseNotes', e.target.value)}
                    />
                    <small className="text-muted">Plain text. Visible in every tenant's sidebar footer.</small>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Updated By</label>
                    <input
                      className="form-control"
                      value={form.updatedBy}
                      onChange={e => set('updatedBy', e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={quickStamp}
                    disabled={saving || !form.version}
                    title="Bump the last-updated timestamp to now without changing the version (useful when you've redeployed the same version with small fixes)"
                  >
                    <i className="bi bi-arrow-clockwise me-1" />Quick re-stamp
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving && <span className="spinner-border spinner-border-sm me-2" />}
                    Save & Broadcast
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                <i className="bi bi-info-circle me-2 text-primary" />
                Current Deployment
              </h6>
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <th style={{ width: 140 }} className="text-muted fw-normal">Version</th>
                    <td className="fw-bold font-monospace">{form.version || '—'}</td>
                  </tr>
                  <tr>
                    <th className="text-muted fw-normal">Build</th>
                    <td className="font-monospace">{form.buildNumber || '—'}</td>
                  </tr>
                  <tr>
                    <th className="text-muted fw-normal">Last Updated</th>
                    <td>{fmtDate(meta.lastUpdatedAt)}</td>
                  </tr>
                  <tr>
                    <th className="text-muted fw-normal">Updated By</th>
                    <td>{meta.lastUpdatedBy || '—'}</td>
                  </tr>
                </tbody>
              </table>
              <hr />
              <h6 className="fw-bold mb-2">
                <i className="bi bi-eye me-2 text-success" />
                How tenants see it
              </h6>
              <p className="text-muted small mb-2">
                The version chip appears at the bottom of every tenant's sidebar.
                Clicking the chip opens a popover with the release notes.
              </p>
              <div style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, fontFamily: 'monospace', fontSize: 11 }}>
                <div style={{ fontWeight: 700, color: '#cbd5e1' }}>v{form.version || '1.0.0'}{form.buildNumber ? ` · build ${form.buildNumber}` : ''}</div>
                <div style={{ color: '#94a3b8', fontSize: 10 }}>Updated {fmtDate(meta.lastUpdatedAt)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertToast
        show={toast.show}
        message={toast.msg}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
}
